import { useCallback } from 'react';

import { useDeleteAnalysisMutation } from '../lib/api/useDeleteAnalysisMutation';

interface UseDeleteAnalysisDialogArgs {
  projectId: string;
  analysisProfileId: string;
  onClose: () => void;
}

export interface UseDeleteAnalysisDialogResult {
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the inline analysis delete confirmation on the artifacts
 * page: fires the delete mutation and surfaces its pending/error state.
 * Intl-free so the view owns copy and this stays unit-testable without
 * rendering the dialog. A 409 (run in progress) surfaces via `submittingError`.
 */
export const useDeleteAnalysisDialog = ({
  projectId,
  analysisProfileId,
  onClose,
}: UseDeleteAnalysisDialogArgs): UseDeleteAnalysisDialogResult => {
  const { mutateAsync, isPending, error, reset } = useDeleteAnalysisMutation();

  const onSubmit = useCallback(async () => {
    await mutateAsync({ projectId, analysisProfileId });
  }, [mutateAsync, projectId, analysisProfileId]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit: !isPending,
  };
};
